import * as React from 'react';
import { Entry } from 'contentful';
import { withTheme } from 'styled-components';
import 'intersection-observer';
import { IProjectContent } from '../../../models/Project';
import styled, { keyframes, ITheme, tstyled } from '../../../theme';
import requestIdleCallback from '../../../requestIdleCallback';

interface IProps {
  projectContent: Entry<IProjectContent>;
}

interface IState {
  isMobile: boolean;
}

const opacityPulse = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const ImgLoadingContainer = tstyled<{ loaded: boolean }, HTMLDivElement>(
  styled.div
)`
  position: absolute;
  opacity: 0;
  width: 100%;
  left: 50%;
  top: 0;
  height: 100%;
  transition: 0.4s ease opacity, 0.8s ease transform 0.35s;
  transform: ${(props) =>
    props.loaded ? 'translate(-50%)' : 'translate(-50%, 10px)'};
  background-color: #efefef;
  contain: strict;
  will-change: opacity, transform;
  animation-name: ${opacityPulse};
  animation-duration: 0.6s;
  animation-iteration-count: infinite;
  animation-timing-function: ease-out;
  animation-direction: alternate;
  animation-delay: 0.4s;
  animation-play-state: ${(props) => (props.loaded ? 'paused' : 'playing')};
`;

const ImgContainerContainer = tstyled<{ loaded: boolean }>(styled.div)`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 50%;
  background-size: cover;
  background-position: center center;
  opacity: ${(props) => (props.loaded ? 1 : 0)};
  transform: ${(props) =>
    props.loaded ? 'translate(-50%)' : 'translate(-50%, 10px)'};
  background-color: #efefef;
  transition: 0.8s ease opacity 0.35s, 0.8s ease transform 0.35s, 0.8s ease box-shadow 0.35s;
  will-change: opacity, transform, box-shadow;
  contain: strict;
  box-shadow: ${(props) =>
    props.loaded ? '5px 8px 150px rgba(0, 0, 0, 0.2)' : 'none'};
  z-index: 1;
`;

const DescriptionContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: -1px;
  width: 101%;
  color: #000;
  font-family: 'Times';
  font-size: 11px;
  letter-spacing: 0;
  text-align: center;
  z-index: 1;
  overflow: hidden;

  @media (min-width: 1800px) {
    font-size: 0.6vw;
  }

  @media (min-width: ${(props) => props.theme.mobileBreakpoint}px) {
    background-color: transparent;
    z-index: 0;
    overflow: visible;
  }
`;

const Description = tstyled<{ loaded: boolean; hovered: boolean }>(styled.div)`
  transform: translateY(${(props) => (props.hovered ? '1%' : '100%')});
  background-color: #fff;
  transition: ${(props) =>
    props.hovered ? '0.4s' : '0.2s'} ease transform, 0s ease opacity 1.5s;
  padding: 5px 10px;
  will-change: transition;
  opacity: ${(props) => (props.loaded ? 1 : 0)};

  @media (min-width: ${(props) => props.theme.mobileBreakpoint}px) {
    transform: translateY(${(props) => (!props.hovered ? '1%' : '100%')});
  }
`;

interface IImgContainerProps {
  url: string;
  natural: boolean;
  width: number;
  height: number;
  description: string | undefined;
  isMobile: boolean;
}

interface IImgContainerState {
  loaded: boolean;
  height: number;
  hovered: boolean;
}

class ImgContainer extends React.PureComponent<
  IImgContainerProps,
  IImgContainerState
> {
  state = {
    loaded: false,
    height: 0,
    hovered: false,
  };

  container: HTMLDivElement;
  img: HTMLImageElement;
  intersectionObserver?: IntersectionObserver;
  mounted = false;

  componentDidMount() {
    this.mounted = true;
    if (this.props.natural) {
      this.handleWindowResize();
      window.addEventListener('resize', this.handleWindowResize);
    }
    requestIdleCallback(this.createIntersectionObserver);
  }

  createIntersectionObserver = () => {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      requestIdleCallback(() => {
        if (entries[0].isIntersecting) {
          this.preload();
          if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
          }
        }
      });
    });
    this.intersectionObserver.observe(this.container);
  };

  preload = () => {
    this.img = new Image();
    this.img.addEventListener('load', this.onLoad);
    this.img.src = this.props.url;
  };

  componentWillUnmount() {
    this.mounted = false;
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    window.removeEventListener('resize', this.handleWindowResize);
  }

  componentDidUpdate(prevProps: IImgContainerProps) {
    const newNatural = prevProps.natural !== this.props.natural;
    if (newNatural && this.props.natural) {
      this.handleWindowResize();
      window.addEventListener('resize', this.handleWindowResize);
    } else if (newNatural && !this.props.natural) {
      window.removeEventListener('resize', this.handleWindowResize);
    }
  }

  handleWindowResize = () => {
    requestAnimationFrame(this.size);
  };

  size = () => {
    this.setState({
      height: this.calcHeight(),
    });
  };

  getRatio = () => {
    return this.props.width / this.props.height;
  };

  calcHeight = () => {
    return this.container.clientWidth / this.getRatio();
  };

  onLoad = () => {
    if (!this.mounted) {
      return;
    }
    requestAnimationFrame(() => {
      this.setState(() => ({ loaded: true }));
    });
    this.img.removeEventListener('load', this.onLoad);
  };

  onMouseEnter = () => {
    this.setState({ hovered: true });
  };

  onMouseLeave = () => {
    this.setState({ hovered: false });
  };

  onClick = () => {
    this.setState((prevState) => ({ hovered: !prevState.hovered }));
  };

  render() {
    const style: Record<string, any> = {
      position: 'relative',
    };
    const imgStyle: Record<string, any> = {};

    if (this.state.loaded) {
      imgStyle.backgroundImage = `url(${this.props.url})`;
    }

    if (this.props.natural) {
      style.height = this.state.height;
      imgStyle.height = this.state.height;
    }

    return (
      <div style={style}>
        <ImgLoadingContainer
          innerRef={(ref) => (this.container = ref)}
          loaded={this.state.loaded}
        />
        <ImgContainerContainer
          innerRef={(ref) => (this.container = ref)}
          style={imgStyle}
          loaded={this.state.loaded}
          onMouseEnter={this.props.isMobile ? undefined : this.onMouseEnter}
          onMouseLeave={this.props.isMobile ? undefined : this.onMouseLeave}
          onClick={this.props.isMobile ? this.onClick : undefined}
        />
        {this.props.description && (
          <DescriptionContainer>
            <Description
              loaded={this.state.loaded}
              hovered={this.state.hovered}
            >
              {this.props.description}
            </Description>
          </DescriptionContainer>
        )}
      </div>
    );
  }
}

const VideoContainerEl = tstyled<IVideoContainerState>(styled.div)`
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: ${(props) => (props.visible ? 'none' : 'translateY(10px)')};
  background-color: #efefef;
  transition: 0.6s ease opacity 0.35s, 0.6s ease transform 0.35s;

  @media (min-width: ${(props) => props.theme.mobileBreakpoint}px) {
    position: relative;
    overflow: hidden;

    video {
      object-fit: cover;
      position: absolute;
      height: 100%;
      width: 100%;
    }
  }

  video {
    width: 100%;
  }
`;

interface IVideoContainerProps {
  url: string;
}
interface IVideoContainerState {
  visible: boolean;
}

class VideoContainer extends React.PureComponent<
  IVideoContainerProps,
  IVideoContainerState
> {
  state = {
    visible: false,
  };

  container: HTMLDivElement;
  intersectionObserver?: IntersectionObserver;
  video: HTMLVideoElement;

  componentDidMount() {
    requestIdleCallback(this.createIntersectionObserver);
  }

  componentWillUnmount() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  createIntersectionObserver = () => {
    if (!this.container) {
      return;
    }
    this.intersectionObserver = new IntersectionObserver((entries) => {
      requestIdleCallback(() => {
        if (entries[0].isIntersecting) {
          this.setState({ visible: true });
          if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
          }
          if (this.video) {
            this.video.play();
          }
        }
      });
    });
    this.intersectionObserver.observe(this.container);
  };

  render() {
    return (
      <VideoContainerEl
        visible={this.state.visible}
        innerRef={(ref) => (this.container = ref)}
      >
        <video
          ref={(ref) => (this.video = ref!)}
          autoPlay={this.state.visible}
          loop
          src={this.props.url}
        />
      </VideoContainerEl>
    );
  }
}

const videoContentTypeRegex = /video/i;
const rawSizeSet: Set<IProjectContent['type']> = new Set([
  '1 / 2' as IProjectContent['type'],
]);
const mobileRawSizeSet: Set<IProjectContent['type']> = new Set([
  '1 / 2' as IProjectContent['type'],
  '1 / 1' as IProjectContent['type'],
]);
class VideoOrImage extends React.PureComponent<
  IProps & { theme: ITheme },
  IState
> {
  state: IState = {
    isMobile: false,
  };

  componentDidMount() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
  }

  determineMobile = () => {
    const shouldBeMobile =
      window.innerWidth < this.props.theme.mobileBreakpoint;
    if (this.state.isMobile !== shouldBeMobile) {
      this.setState({ isMobile: shouldBeMobile });
    }
  };

  onResize = () => {
    requestAnimationFrame(this.determineMobile);
  };

  render() {
    const { projectContent } = this.props;
    const { isMobile } = this.state;

    return projectContent.fields.images.map((img) => {
      const isVideo = videoContentTypeRegex.test(img.fields.file.contentType);
      if (isVideo) {
        if (isMobile) {
          return null;
        }
        return <VideoContainer key={img.sys.id} url={img.fields.file.url} />;
      }

      const set = isMobile ? mobileRawSizeSet : rawSizeSet;

      return (
        <ImgContainer
          natural={set.has(projectContent.fields.type)}
          key={img.sys.id}
          url={img.fields.file.url}
          width={img.fields.file.details.image.width}
          height={img.fields.file.details.image.height}
          description={img.fields.description}
          isMobile={isMobile}
        />
      );
    });
  }
}

export default withTheme(VideoOrImage);



// WEBPACK FOOTER //
// ./src/scenes/Project/ProjectContent/VideoOrImage.tsx